<template>
  <data-table-wrapper
    :filter-config="filterConfig"
    :default-sort="{ by: 'lastName', desc: false }"
    :default-items-per-page="25"
    :enable-search="true"
    :search-config="{
      urlKey: 'search',
      placeholder: 'Suchen...',
      debounce: 300,
    }"
    @filters-changed="onFiltersChanged"
  >
    <template
      #default="{
        tableProps,
        tableEvents,
        filters,
        updateFilter,
        searchValue,
        updateSearch,
      }"
    >
      <div>
        <v-data-table
          v-bind="tableProps"
          v-on="tableEvents"
          :headers="tableHeaders"
          :items="normalizedPersonnelRecords"
          item-key="user.uid"
          :search="searchValue"
          multi-sort
          :footer-props="footerProps"
          :group-by="
            resolvedGroupBy && resolvedGroupBy.value ? '_groupKey' : null
          "
          class="elevation-2 personnel-records-table--sticky-columns"
          max-height="800px auto"
          @click:row="routeDetails"
          :mobile-breakpoint="0"
          no-data-text="Keine Personen vorhanden"
          dense
          no-results-text="Keine Personen gefunden"
        >
          <!-- show-select -->
          <template v-slot:top>
            <table-header
              :activeDataViewValue="filters.dataView"
              @active-data-view-updated="updateFilter('dataView', $event)"
              :dataViews="dataViews"
              :searchValue="searchValue"
              @search-updated="updateSearch($event)"
              showTableColumnSelector
              :allColumnHeaders="filteredHeaders"
              :tableHeaders="tableHeaders"
              @table-headers-updated="tableHeaders = $event"
              :groupByItems="groupByItems"
              :groupBySelectorValue="resolvedGroupBy"
              @group-by-selector-updated="updateFilter('groupBy', $event)"
              allowExport
            ></table-header>
          </template>
          <template v-slot:group.header="{ items, isOpen, toggle }">
            <th :colspan="tableHeaders.length">
              <div class="my-1">
                <v-icon @click="toggle" class="mr-1"
                  >{{ isOpen ? "mdi-chevron-up" : "mdi-chevron-down" }}
                </v-icon>
                <span>
                  {{ stripPrefix(items[0]._groupKey) }}
                </span>
                <span class="grey--text ml-2">({{ items.length }})</span>
              </div>
            </th>
          </template>
          <template v-slot:[`item.personnelNumber`]="{ item }">
            <pre>{{ item.personnelNumber }}</pre>
          </template>
          <template v-slot:[`item.rank.title`]="{ item }">
            <v-chip outlined label v-if="item.rank.title" small>
              <img
                v-if="item.rank.badgeUrl.size70x70"
                :src="item.rank.badgeUrl.size70x70"
                contain
                height="20px"
                class="mr-2 py-1"
              />
              <span class="font-weight-medium">{{ item.rank.shortTitle }}</span>
            </v-chip>
          </template>
          <template v-slot:[`item.dutyPosition.title`]="{ item }">
            <v-chip outlined label v-if="item.dutyPosition.title" small>
              <img
                v-if="item.dutyPosition.badgeUrl.size70x70"
                :src="item.dutyPosition.badgeUrl.size70x70"
                contain
                height="20px"
                class="mr-2 py-1"
              />
              <span class="font-weight-medium">{{
                item.dutyPosition.shortTitle
              }}</span>
            </v-chip>
          </template>
          <template v-slot:[`item.birthdate`]="{ item }">
            <span
              v-if="item.birthdate"
              :class="getBirthdayColor(item.birthdate.toDate()) + '--text'"
            >
              {{ timestampConverter(item.birthdate) }} ({{
                getAge(item.birthdate.toDate())
              }})
            </span>
          </template>
          <template v-slot:[`item.status`]="{ item }">
            <personnel-record-status-chip :status="item.status" small>
            </personnel-record-status-chip>
          </template>
          <template v-slot:[`item.contact.personal.address.street`]="{ item }">
            <span
              v-if="
                item.contact &&
                item.contact.personal &&
                item.contact.personal.address
              "
            >
              {{ formatAddress(item.contact.personal.address) }}
            </span>
          </template>
          <template v-slot:[`item.contact.business.address.street`]="{ item }">
            <span
              v-if="
                item.contact &&
                item.contact.business &&
                item.contact.business.address
              "
            >
              {{ formatAddress(item.contact.business.address) }}
            </span>
          </template>
          <!-- Personal Contact -->
          <template v-slot:[`item.contact.personal.phone`]="{ item }">
            <contact-link
              :value="getContactValue(item, 'contact.personal.phone')"
              link-type="tel"
            />
          </template>
          <template v-slot:[`item.contact.personal.mobile`]="{ item }">
            <contact-link
              :value="getContactValue(item, 'contact.personal.mobile')"
              link-type="tel"
            />
          </template>
          <template v-slot:[`item.contact.personal.email`]="{ item }">
            <contact-link
              :value="getContactValue(item, 'contact.personal.email')"
              link-type="mailto"
            />
          </template>
          <!-- Business Contact -->
          <template v-slot:[`item.contact.business.phone`]="{ item }">
            <contact-link
              :value="getContactValue(item, 'contact.business.phone')"
              link-type="tel"
            />
          </template>
          <template v-slot:[`item.contact.business.mobile`]="{ item }">
            <contact-link
              :value="getContactValue(item, 'contact.business.mobile')"
              link-type="tel"
            />
          </template>
          <template v-slot:[`item.contact.business.email`]="{ item }">
            <contact-link
              :value="getContactValue(item, 'contact.business.email')"
              link-type="mailto"
            />
          </template>
          <!-- Station Contact -->
          <template v-slot:[`item.contact.station.phone`]="{ item }">
            <contact-link
              :value="getContactValue(item, 'contact.station.phone')"
              link-type="tel"
            />
          </template>
          <template v-slot:[`item.contact.station.mobile`]="{ item }">
            <contact-link
              :value="getContactValue(item, 'contact.station.mobile')"
              link-type="tel"
            />
          </template>
          <template v-slot:[`item.contact.station.email`]="{ item }">
            <contact-link
              :value="getContactValue(item, 'contact.station.email')"
              link-type="mailto"
            />
          </template>
        </v-data-table>
      </div>
    </template>
  </data-table-wrapper>
</template>

<script>
import TableHeader from "@/components/_system/helpers/TableHeader.vue";
import PersonnelRecordStatusChip from "@/components/member-management/personnel-records/PersonnelRecordStatusChip.vue";
import DataTableWrapper from "@/components/_system/DataTableWrapper.vue";
import ContactLink from "@/components/member-management/personnel-records/ContactLink.vue";
import { MEMBER_MANAGEMENT_PERSONNEL_RECORDS_CONTACT_read } from "@/data/permission-types.js";

export default {
  name: "personnel-records-data-table",
  props: {
    personnelRecords: {
      type: Array,
      required: true,
    },
  },
  components: {
    TableHeader,
    PersonnelRecordStatusChip,
    DataTableWrapper,
    ContactLink,
  },
  data() {
    return {
      footerProps: {
        "items-per-page-options": [15, 25, 50, 100, -1],
      },
      filterConfig: {
        dataView: {
          default: "active",
          urlKey: "view",
        },
        groupBy: {
          default: "division.title",
          urlKey: "groupBy",
        },
      },
      currentFilters: {
        dataView: "active",
        groupBy: "division.title",
      },
      dataViews: [
        { text: "Alle", value: "all" },
        { text: "Aktiv", value: "active" },
        { text: "Beurlaubt", value: "onLeave" },
        { text: "Im Übertritt", value: "transfer" },
        { text: "Ausgetreten", value: "resigned" },
        { text: "Entlassen", value: "dismissed" },
        { text: "Verstorben", value: "deceased" },
      ],
      groupByItems: [
        { text: "Abteilung", value: "division.title", icon: "mdi-home-group" },
        {
          text: "Dienstgrad",
          value: "rank.title",
          icon: "mdi-chevron-double-up",
        },
        {
          text: "Dienststellung",
          value: "dutyPosition.title",
          icon: "mdi-account-tie",
        },
        { text: "Status", value: "status", icon: "mdi-account-check" },
      ],
      statusOptions: [
        { text: "Aktiv", value: "Aktiv" },
        { text: "Im Übertritt", value: "Im Übertritt" },
        { text: "Beurlaubt", value: "Beurlaubt" },
        { text: "Ausgetreten", value: "Ausgetreten" },
        { text: "Entlassen", value: "Entlassen" },
        { text: "Verstorben", value: "Verstorben" },
      ],
      headers: [
        {
          sortKey: 0,
          text: "Personalnummer",
          value: "personnelNumber",
          group: "1_Stammdaten",
          default: true,
          divider: true,
        },
        {
          sortKey: 2,
          text: "Titel",
          value: "title",
          group: "1_Stammdaten",
          default: false,
        },
        {
          sortKey: 3,
          text: "Vorname",
          value: "firstName",
          group: "1_Stammdaten",
          default: true,
        },
        {
          sortKey: 4,
          text: "Nachname",
          value: "lastName",
          group: "1_Stammdaten",
          default: true,
          divider: true,
        },
        {
          sortKey: 5,
          text: "Dienstgrad",
          value: "rank.title",
          group: "1_Stammdaten",
          default: true,
          align: "center",
          divider: true,
        },
        {
          sortKey: 6,
          text: "Dienststellung",
          value: "dutyPosition.title",
          group: "1_Stammdaten",
          default: true,
          align: "center",
          divider: true,
        },
        {
          sortKey: 7,
          text: "Abteilung",
          value: "division.title",
          group: "1_Stammdaten",
          default: true,
          divider: true,
        },
        {
          sortKey: 8,
          text: "Geburtsdatum",
          value: "birthdate",
          group: "1_Stammdaten",
          default: true,
          divider: true,
        },
        {
          sortKey: 9,
          text: "Geburtsort",
          value: "birthplace",
          group: "1_Stammdaten",
          default: false,
          divider: true,
        },
        {
          sortKey: 10,
          text: "Status",
          value: "status",
          group: "1_Stammdaten",
          divider: true,
          default: true,
          filter: (value) => {
            if (!this.currentFilters.dataView) return true;
            if (this.currentFilters.dataView === "all") return true;
            return value === this.currentFilters.dataView;
          },
        },
        {
          sortKey: 11,
          text: "Straße, Nr.",
          value: "contact.personal.address.street",
          group: "2_Kontaktdaten",
          default: false,
          divider: true,
        },
        {
          sortKey: 12,
          text: "Postleitzahl",
          value: "contact.personal.address.postalCode",
          group: "2_Kontaktdaten",
          default: false,
        },
        {
          sortKey: 13,
          text: "Stadt",
          value: "contact.personal.address.city",
          group: "2_Kontaktdaten",
          default: false,
          divider: true,
        },
        {
          sortKey: 14,
          text: "Telefon (privat)",
          value: "contact.personal.phone",
          group: "2_Kontaktdaten",
          default: false,
          divider: true,
        },
        {
          sortKey: 15,
          text: "Mobil (privat)",
          value: "contact.personal.mobile",
          group: "2_Kontaktdaten",
          default: false,
          divider: true,
        },
        {
          sortKey: 16,
          text: "E-Mail (privat)",
          value: "contact.personal.email",
          group: "2_Kontaktdaten",
          default: false,
          divider: true,
        },
        {
          sortKey: 17,
          text: "Firma/Organisation",
          value: "contact.business.name",
          group: "2_Kontaktdaten",
          default: false,
          divider: true,
        },
        {
          sortKey: 18,
          text: "Straße, Nr. (geschäftlich)",
          value: "contact.business.address.street",
          group: "2_Kontaktdaten",
          default: false,
          divider: true,
        },
        {
          sortKey: 19,
          text: "Postleitzahl (geschäftlich)",
          value: "contact.business.address.postalCode",
          group: "2_Kontaktdaten",
          default: false,
        },
        {
          sortKey: 20,
          text: "Stadt (geschäftlich)",
          value: "contact.business.address.city",
          group: "2_Kontaktdaten",
          default: false,
          divider: true,
        },
        {
          sortKey: 21,
          text: "Telefon (geschäftlich)",
          value: "contact.business.phone",
          group: "2_Kontaktdaten",
          default: false,
          divider: true,
        },
        {
          sortKey: 22,
          text: "Mobil (geschäftlich)",
          value: "contact.business.mobile",
          group: "2_Kontaktdaten",
          default: false,
          divider: true,
        },
        {
          sortKey: 23,
          text: "E-Mail (geschäftlich)",
          value: "contact.business.email",
          group: "2_Kontaktdaten",
          default: false,
          divider: true,
        },
        {
          sortKey: 24,
          text: "Telefon (Dienststelle)",
          value: "contact.station.phone",
          group: "2_Kontaktdaten",
          default: false,
          divider: true,
        },
        {
          sortKey: 25,
          text: "Mobil (Dienststelle)",
          value: "contact.station.mobile",
          group: "2_Kontaktdaten",
          default: false,
          divider: true,
        },
        {
          sortKey: 26,
          text: "E-Mail (Dienststelle)",
          value: "contact.station.email",
          group: "2_Kontaktdaten",
          default: false,
          divider: true,
        },
      ],
      tableHeaders: [
        {
          sortKey: 0,
          text: "Personalnummer",
          value: "personnelNumber",
          group: "1_Stammdaten",
          default: true,
          divider: true,
        },
        {
          sortKey: 3,
          text: "Vorname",
          value: "firstName",
          group: "1_Stammdaten",
          default: true,
        },
        {
          sortKey: 4,
          text: "Nachname",
          value: "lastName",
          group: "1_Stammdaten",
          default: true,
          divider: true,
        },
        {
          sortKey: 5,
          text: "Dienstgrad",
          value: "rank.title",
          group: "1_Stammdaten",
          default: true,
          divider: true,
        },
        {
          sortKey: 6,
          text: "Dienststellung",
          value: "dutyPosition.title",
          group: "1_Stammdaten",
          default: true,
          divider: true,
        },
        {
          sortKey: 7,
          text: "Abteilung",
          value: "division.title",
          group: "1_Stammdaten",
          default: true,
          divider: true,
        },
        {
          sortKey: 8,
          text: "Geburtsdatum",
          value: "birthdate",
          group: "1_Stammdaten",
          default: true,
          divider: true,
        },
        {
          sortKey: 10,
          text: "Status",
          value: "status",
          group: "1_Stammdaten",
          default: true,
          divider: true,
          filter: (value) => {
            if (!this.currentFilters.dataView) return true;
            if (this.currentFilters.dataView === "all") return true;
            return value === this.currentFilters.dataView;
          },
        },
        {
          sortKey: 10.5,
          text: "Labels",
          value: "labels",
          group: "1_Stammdaten",
          default: false,
          sortable: false,
          divider: true,
        },
      ],
    };
  },
  computed: {
    hasContactPermission() {
      return this.$store.getters["organization/checkPermissionByID"](
        MEMBER_MANAGEMENT_PERSONNEL_RECORDS_CONTACT_read
      );
    },
    // Reconstruct full groupBy object from URL value by matching against groupByItems
    resolvedGroupBy() {
      const groupBy = this.currentFilters.groupBy;

      // Handle null or 'none' explicitly
      if (!groupBy || groupBy === "none") {
        return null;
      }

      // If it's already an object, return it
      if (typeof groupBy === "object" && groupBy.value) {
        return groupBy;
      }

      // If it's a string, find matching item in groupByItems
      if (typeof groupBy === "string") {
        return this.groupByItems.find((item) => item.value === groupBy) || null;
      }

      return null;
    },

    filteredHeaders() {
      // Filter out contact-related headers if user doesn't have permission
      if (this.hasContactPermission) {
        return this.headers;
      }
      return this.headers.filter(
        (header) => !header.group || !header.group.includes("Kontaktdaten")
      );
    },
    normalizedPersonnelRecords() {
      return this.personnelRecords.map((record) => {
        const normalized = { ...record };
        const groupBy = this.resolvedGroupBy;
        if (groupBy && groupBy.value) {
          const groupField = groupBy.value;

          if (groupField === "division.title") {
            normalized._groupKey =
              this.getGroupPrefix(record.division) +
              "_" +
              (record.division?.title || "Keine Abteilung");
          } else if (groupField === "rank.title") {
            normalized._groupKey =
              this.getGroupPrefix(record.rank) +
              "_" +
              (record.rank?.title || "Kein Dienstgrad");
          } else if (groupField === "dutyPosition.title") {
            normalized._groupKey =
              this.getGroupPrefix(record.dutyPosition) +
              "_" +
              (record.dutyPosition?.title || "Keine Dienststellung");
          } else if (groupField === "status") {
            normalized._groupKey =
              this.getStatusPrefix(record.status) +
              "_" +
              this.getStatusText(record.status);
          } else {
            const keys = groupField.split(".");
            let value = record;
            for (const key of keys) {
              value = value?.[key];
            }
            normalized._groupKey = value || "Keine Zuordnung";
          }
        }
        return normalized;
      });
    },
  },
  methods: {
    onFiltersChanged(filters) {
      this.currentFilters = { ...filters };
    },
    updateDataView(value) {
      this.currentFilters.dataView = value;
    },
    updateGroupBySelector(value) {
      // Convert null to 'none' to explicitly indicate no grouping in URL
      this.currentFilters.groupBy = value === null ? "none" : value;
    },
    getGroupPrefix(item) {
      if (!item || !item.sortKey) return "9999";
      return item.sortKey.toString().padStart(4, "0");
    },
    getStatusPrefix(status) {
      const statusOrder = {
        active: "01",
        onLeave: "02",
        transfer: "03",
        resigned: "04",
        dismissed: "05",
        deceased: "06",
      };
      return statusOrder[status] || "99";
    },
    getStatusText(status) {
      const statusText = {
        active: "Aktiv",
        onLeave: "Beurlaubt",
        transfer: "Im Übertritt",
        resigned: "Ausgetreten",
        dismissed: "Entlassen",
        deceased: "Verstorben",
      };
      return statusText[status] || "Unbekannt";
    },
    stripPrefix(groupKey) {
      if (!groupKey) return "";
      const underscoreIndex = groupKey.indexOf("_");
      return underscoreIndex !== -1
        ? groupKey.substring(underscoreIndex + 1)
        : groupKey;
    },
    getAge(birthdate) {
      const today = new Date();
      const birthDate = new Date(birthdate);
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (
        monthDiff < 0 ||
        (monthDiff === 0 && today.getDate() < birthDate.getDate())
      ) {
        age--;
      }
      return age;
    },
    getDaysUntilBirthday(birthdate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const birthDate = new Date(birthdate);
      const currentYear = today.getFullYear();

      // Create birthday for current year
      const birthdayThisYear = new Date(
        currentYear,
        birthDate.getMonth(),
        birthDate.getDate()
      );
      birthdayThisYear.setHours(0, 0, 0, 0);

      // If birthday already passed this year, check next year
      const birthdayNextYear = new Date(
        currentYear + 1,
        birthDate.getMonth(),
        birthDate.getDate()
      );
      birthdayNextYear.setHours(0, 0, 0, 0);

      const diffThisYear = Math.round(
        (birthdayThisYear - today) / (1000 * 60 * 60 * 24)
      );
      const diffNextYear = Math.round(
        (birthdayNextYear - today) / (1000 * 60 * 60 * 24)
      );

      // Return the closest upcoming birthday
      return Math.abs(diffThisYear) <= Math.abs(diffNextYear)
        ? diffThisYear
        : diffNextYear;
    },
    getBirthdayColor(birthdate) {
      const daysUntil = this.getDaysUntilBirthday(birthdate);
      if (daysUntil === 0) return "red";
      if (Math.abs(daysUntil) <= 7) return "purple";
      return "";
    },
    formatAddress(address) {
      if (!address) return "";
      const parts = [];
      if (address.street) parts.push(address.street);
      if (address.streetNumber) parts.push(address.streetNumber);
      return parts.join(" ");
    },
    getContactValue(item, path) {
      // Helper to safely get nested contact values
      const keys = path.split(".");
      let value = item;
      for (const key of keys) {
        value = value?.[key];
        if (value === undefined || value === null) return null;
      }
      return value;
    },
    timestampConverter(timestamp) {
      const options = {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      };
      return timestamp.toDate().toLocaleDateString("de-DE", options);
    },

    routeDetails(item) {
      this.$router.push({
        name: "member-management-personnel-records-details-general",
        params: {
          organizationId: this.$route.params.organizationId,
          itemId: item.user.uid,
        },
      });
    },
    initials(fullName) {
      const namesArray = fullName.trim().split(" ");
      if (namesArray.length === 1) return `${namesArray[0].charAt(0)}`;
      else
        return `${namesArray[0].charAt(0)}${namesArray[
          namesArray.length - 1
        ].charAt(0)}`;
    },
  },
};
</script>

<style scoped>
/* Sticky columns implementation */
.personnel-records-table--sticky-columns ::v-deep thead th:nth-child(1),
.personnel-records-table--sticky-columns ::v-deep tbody td:nth-child(1) {
  position: sticky !important;
  left: 0;
  z-index: 3 !important;
  background-color: white;
}

.personnel-records-table--sticky-columns ::v-deep thead th:nth-child(2),
.personnel-records-table--sticky-columns ::v-deep tbody td:nth-child(2) {
  position: sticky !important;
  left: 100px; /* Width of first column */
  z-index: 3 !important;
  background-color: white;
}

.personnel-records-table--sticky-columns ::v-deep thead th:nth-child(3),
.personnel-records-table--sticky-columns ::v-deep tbody td:nth-child(3) {
  position: sticky !important;
  left: 200px; /* Width of first + second column */
  z-index: 3 !important;
  background-color: white;
}

/* Higher z-index for header cells */
.personnel-records-table--sticky-columns ::v-deep thead th:nth-child(1),
.personnel-records-table--sticky-columns ::v-deep thead th:nth-child(2),
.personnel-records-table--sticky-columns ::v-deep thead th:nth-child(3) {
  z-index: 4 !important;
}

/* Shadow effect for sticky columns */
.personnel-records-table--sticky-columns ::v-deep tbody td:nth-child(3) {
  box-shadow: 2px 0 4px -2px rgba(0, 0, 0, 0.2);
}

/* Handle hover state background */
.personnel-records-table--sticky-columns
  ::v-deep
  tbody
  tr:hover
  td:nth-child(1),
.personnel-records-table--sticky-columns
  ::v-deep
  tbody
  tr:hover
  td:nth-child(2),
.personnel-records-table--sticky-columns
  ::v-deep
  tbody
  tr:hover
  td:nth-child(3) {
  background-color: #eeeeee;
}

/* Handle grouped rows background */
.personnel-records-table--sticky-columns
  ::v-deep
  tbody
  tr.v-data-table__selected
  td:nth-child(1),
.personnel-records-table--sticky-columns
  ::v-deep
  tbody
  tr.v-data-table__selected
  td:nth-child(2),
.personnel-records-table--sticky-columns
  ::v-deep
  tbody
  tr.v-data-table__selected
  td:nth-child(3) {
  background-color: inherit;
}

/* Sticky group headers */
.personnel-records-table--sticky-columns
  ::v-deep
  tbody
  tr.v-row-group__header
  th {
  position: sticky !important;
  left: 0 !important;
  z-index: 3 !important;
}

/* Dark mode support */
.theme--dark
  .personnel-records-table--sticky-columns
  ::v-deep
  thead
  th:nth-child(1),
.theme--dark
  .personnel-records-table--sticky-columns
  ::v-deep
  tbody
  td:nth-child(1),
.theme--dark
  .personnel-records-table--sticky-columns
  ::v-deep
  thead
  th:nth-child(2),
.theme--dark
  .personnel-records-table--sticky-columns
  ::v-deep
  tbody
  td:nth-child(2),
.theme--dark
  .personnel-records-table--sticky-columns
  ::v-deep
  thead
  th:nth-child(3),
.theme--dark
  .personnel-records-table--sticky-columns
  ::v-deep
  tbody
  td:nth-child(3) {
  background-color: #1e1e1e;
}

.theme--dark
  .personnel-records-table--sticky-columns
  ::v-deep
  tbody
  tr:hover
  td:nth-child(1),
.theme--dark
  .personnel-records-table--sticky-columns
  ::v-deep
  tbody
  tr:hover
  td:nth-child(2),
.theme--dark
  .personnel-records-table--sticky-columns
  ::v-deep
  tbody
  tr:hover
  td:nth-child(3) {
  background-color: #2d2d2d;
}
</style>
